import { useEffect, useMemo, useState } from 'react';
import api from '../../../services/api';
import { Skeleton } from '../../ui/Skeleton';
import WarehouseMap from '../map/WarehouseMap';
import { BuildRouteMapOverlay } from './routeGeometry';

/**
 * A ROTA DESENHADA SOBRE O MAPA DO GALPÃO (M5-3).
 *
 * Palavras do dono, 11/08: *"o desenho da rota DEVE usar o sistema de mapa para
 * calcular rota, mesmo que as rotas não estejam dispostas lado a lado
 * corretamente, devemos ainda sim usar o mapa para calcular e exibir."*
 *
 * ── O que morreu aqui ───────────────────────────────────────────────────────
 * O `RouteDiagram` — um SVG paralelo, no referencial do motor, com eixos de rua
 * e travessa frontal desenhados do zero. Ele era uma SEGUNDA descrição do
 * galpão: o dono editava um desenho e a rota aparecia noutro. Agora é o MESMO
 * `WarehouseMap` do editor (modo `route`), com o percurso por cima.
 *
 * ── Este componente não calcula nada ────────────────────────────────────────
 * O motor devolve a polilinha, as pernas de 90° e a perna final em METROS, e o
 * comparador devolve `map_cell_m` — o câmbio com que ele MULTIPLICOU as células
 * do mapa. `BuildRouteMapOverlay` faz a divisão inversa e nada mais. Se o mapa
 * está torto, a rota sai torta: o dono aceitou a imprecisão do desenho, e
 * "consertar" a linha aqui faria a tela discordar do número da tabela.
 *
 * ── Degradação ──────────────────────────────────────────────────────────────
 * O layout vem de `GET /warehouse/map-layout`, que exige `wms:manage:locations`
 * — permissão que um Supervisor pode não ter. Sem o mapa, o painel diz por que
 * não há desenho (com a mensagem do servidor) em vez de sumir em silêncio: a
 * tabela do comparador continua valendo, só o desenho é que não existe.
 */

// O layout é dado de desenho (muda raro) e as duas superfícies do console
// (alocação e ficha da OS) pedem o mesmo: uma busca por sessão, compartilhada.
let cachedLayout = null;
let layoutResolved = false;
let pendingLayoutFetch = null;

function fetchMapLayout() {
    if (layoutResolved) {
        return Promise.resolve(cachedLayout);
    }
    if (!pendingLayoutFetch) {
        // O envelope é `{layout, updated_at}` e `layout` é NULO quando o galpão
        // ainda não foi desenhado — ler o envelope como se fosse o desenho
        // faria o mapa tentar renderizar `{updated_at}` como layout.
        pendingLayoutFetch = api
            .getWarehouseMapLayout()
            .then((payload) => {
                cachedLayout = payload?.layout ?? null;
                layoutResolved = true;
                return cachedLayout;
            })
            .finally(() => {
                pendingLayoutFetch = null;
            });
    }
    return pendingLayoutFetch;
}

const FIT_HEIGHT = 440;

const Foot = ({ children }) => <span className="text-xs text-mono-500 dark:text-mono-400">{children}</span>;

const RouteMapView = ({
    comparison,
    result = null,
    stops = [],
    label,
    manualMode = false,
    manualSequence = [],
    onToggleStop = null,
}) => {
    const [layout, setLayout] = useState(cachedLayout);
    const [loadError, setLoadError] = useState(null);
    const [loading, setLoading] = useState(!layoutResolved);

    useEffect(() => {
        if (layoutResolved) {
            return undefined;
        }
        let alive = true;
        setLoading(true);
        fetchMapLayout()
            .then((data) => {
                if (alive) {
                    setLayout(data);
                    setLoadError(null);
                }
            })
            .catch((error) => {
                if (alive) {
                    setLoadError(error?.message || label('wms.common.load_error'));
                }
            })
            .finally(() => {
                if (alive) {
                    setLoading(false);
                }
            });
        return () => {
            alive = false;
        };
    }, [label]);

    const overlay = useMemo(
        () => BuildRouteMapOverlay({ comparison, result, stops, manualMode, manualSequence }),
        [comparison, result, stops, manualMode, manualSequence],
    );

    const text = (key) => label(key);
    const sideName = overlay.final_leg?.side ? text(`wms.route.side.${overlay.final_leg.side}`) : null;

    if (loading) {
        return <Skeleton className="h-64 w-full" />;
    }

    // Sem layout (sem permissão, sem desenho salvo ou sem o câmbio da célula) o
    // painel EXPLICA — nunca desenha um galpão vazio como se fosse o do dono.
    if (loadError || !layout) {
        return (
            <div className="rounded-lg border border-dashed border-mono-300 p-4 text-sm text-mono-600 dark:border-mono-700 dark:text-mono-400">
                <p className="font-medium text-black dark:text-white">{text('wms.route.map_unavailable')}</p>
                {loadError ? <p className="mt-1">{loadError}</p> : null}
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <WarehouseMap
                mode="route"
                layout={layout}
                fitHeight={FIT_HEIGHT}
                route={overlay}
                onRouteStopClick={manualMode ? onToggleStop : null}
            />

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-mono-200 px-3 py-2 dark:border-mono-800">
                <Foot>{label('wms.map.orientation')}</Foot>
                {result?.start_code ? (
                    <Foot>{`${text('wms.route.start_at')}: ${result.start_code}`}</Foot>
                ) : null}
                {/* Onde a rota TERMINA é a origem da PRÓXIMA (regra 5): não
                    existe mais retorno presumido à cabeceira. */}
                {result?.end_code ? (
                    <Foot>{`${text('wms.route.end_at')}: ${result.end_code} — ${text('wms.route.end_hint')}`}</Foot>
                ) : null}
                {overlay.final_leg ? (
                    <span className="text-xs font-medium text-warning-700 dark:text-warning-400">
                        {`${label('wms.route.facing')}: ${sideName ?? '—'}`}
                        {overlay.final_leg.code ? ` (${overlay.final_leg.code})` : ''}
                    </span>
                ) : null}
                {manualMode ? (
                    <span className="text-xs font-medium text-info-700 dark:text-info-300">
                        {label('wms.route.manual_hint')}
                    </span>
                ) : (
                    <Foot>{text('wms.route.map_hint')}</Foot>
                )}
            </div>
        </div>
    );
};

export default RouteMapView;
